import { useCallback, useRef, useState } from 'react';

import { useInterval } from '~shared/hooks';

/** How fast to change when pressed. */
const CONTINUOUS_CHANGE_INTERVAL = 50;
/** Delay before auto-changing. */
const CONTINUOUS_CHANGE_DELAY = 300;

export type SpinAction = 'increment' | 'decrement';
type ActionFn = (step?: any) => void;

/**
 * Spinning (incrementing/decrementing) the NumericInput value while spin
 * buttons are pressed.
 *
 * @param increment the function to increment
 * @param decrement the function to decrement
 */
export function useSpinner(increment: ActionFn, decrement: ActionFn) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [action, setAction] = useState<SpinAction | null>(null);
  const [firstPress, setFirstPress] = useState(true);

  // Store the timeout instance id in a ref, so we can clear the timeout later.
  const timeoutRef = useRef<any>(null);

  const removeTimeout = () => clearTimeout(timeoutRef.current);

  useInterval(
    () => {
      if (action === 'increment') {
        increment();
      }
      if (action === 'decrement') {
        decrement();
      }
    },
    isSpinning ? CONTINUOUS_CHANGE_INTERVAL : null
  );

  const up = useCallback(() => {
    if (firstPress) {
      increment();
    }

    // After a delay, keep incrementing at interval.
    timeoutRef.current = setTimeout(() => {
      setFirstPress(false);
      setIsSpinning(true);
      setAction('increment');
    }, CONTINUOUS_CHANGE_DELAY);
  }, [increment, firstPress]);

  const down = useCallback(() => {
    if (firstPress) {
      decrement();
    }

    // After a delay, keep decrementing at interval.
    timeoutRef.current = setTimeout(() => {
      setFirstPress(false);
      setIsSpinning(true);
      setAction('decrement');
    }, CONTINUOUS_CHANGE_DELAY);
  }, [decrement, firstPress]);

  /** Stop spinning; used for mouseUp, touchEnd, etc. */
  const stop = useCallback(() => {
    setFirstPress(true);
    setIsSpinning(false);
    removeTimeout();
  }, []);

  // If the component unmounts while spinning, clear the timeout.
  removeTimeout();

  return { up, down, stop };
}
